import { apiClient, getErrorMessage } from '../../utils/apiClient';
import { logger } from '../../utils/logger';
import {
  API_CHAT_CONNECTORS,
  API_CHAT_COMPLETIONS,
  API_CHAT_MY_CONNECTORS,
  API_CHAT_MY_CONNECTORS_ID,
  API_CHAT_MY_USAGE,
  API_CHAT_COMPACT,
} from '../../constants/apiConstants';
import { ChatConnector } from '../../features/chat/types/chatTypes';

// ============================================================================
// Types
// ============================================================================

export interface ChatByokCreateRequest {
  providerCode: string;
  baseUrl?: string;
  apiKey: string;
  extraConfig?: string;
}

// ============================================================================
// Service
// ============================================================================

const chatService = {
  async getUserConnectors(): Promise<ChatConnector[]> {
    try {
      const response = await apiClient.get(API_CHAT_CONNECTORS);
      console.log('[chatService] getUserConnectors raw response:', response.data);
      if (response.data?.isSuccess) {
        return response.data.data ?? [];
      }
      return [];
    } catch (error) {
      console.error('[chatService] getUserConnectors error:', error);
      logger.error('chatService.getUserConnectors', getErrorMessage(error));
      return [];
    }
  },

  async getMyConnectors(): Promise<ChatConnector[]> {
    try {
      const response = await apiClient.get(API_CHAT_MY_CONNECTORS);
      if (response.data?.isSuccess) {
        return response.data.data ?? [];
      }
      return [];
    } catch (error) {
      logger.error('chatService.getMyConnectors', getErrorMessage(error));
      return [];
    }
  },

  async createByokConnector(request: ChatByokCreateRequest) {
    try {
      const response = await apiClient.post(API_CHAT_MY_CONNECTORS, request);
      return response.data;
    } catch (error) {
      logger.error('chatService.createByokConnector', getErrorMessage(error));
      throw error;
    }
  },

  async updateByokConnector(id: number, request: ChatByokCreateRequest) {
    try {
      const response = await apiClient.put(API_CHAT_MY_CONNECTORS_ID(id), request);
      return response.data;
    } catch (error) {
      logger.error('chatService.updateByokConnector', getErrorMessage(error));
      throw error;
    }
  },

  async deleteByokConnector(id: number) {
    try {
      const response = await apiClient.delete(API_CHAT_MY_CONNECTORS_ID(id));
      return response.data;
    } catch (error) {
      logger.error('chatService.deleteByokConnector', getErrorMessage(error));
      throw error;
    }
  },

  async getMyUsage(params?: { from?: string; to?: string; connectorId?: number }) {
    try {
      const response = await apiClient.get(API_CHAT_MY_USAGE, { params });
      return response.data;
    } catch (error) {
      logger.error('chatService.getMyUsage', getErrorMessage(error));
      return null;
    }
  },

  async compactSession(sessionId: number, connectorId: number): Promise<boolean> {
    try {
      const response = await apiClient.post(API_CHAT_COMPACT, { sessionId, connectorId });
      return response.data?.isSuccess === true;
    } catch (error) {
      logger.error('chatService.compactSession', getErrorMessage(error));
      return false;
    }
  },

  getCompletionUrl(): string {
    const baseUrl = apiClient.defaults.baseURL ?? '';
    return `${baseUrl}${API_CHAT_COMPLETIONS}`;
  },
};

export default chatService;
